<template>
	<b-modal
		dialog-class="_modalReservations"
		id="modal-reservations-view"
		scrollable
		no-fade
		ok-only
		:ok-title="$t('global.save')"
		:hide-footer="hasLimitedData"
		size="xl"
		v-model="showModal"
		body-class="position-static"
		@ok="trySubmitForm"
	>
		<b-overlay :show="loading" no-wrap rounded></b-overlay>
		<template #modal-title>
			<span v-if="model.id > 0">
				{{$t('bookings.booking')}} #{{model.id}} <b-badge :variant="model.status == 'active' ? 'success':'danger'">{{statusLabel}}</b-badge>
				<b-badge class="ml-2" variant="default" v-if="model.operatedBy === 'sc'"><ScIcon /> {{$t('bookings.shuttleCentralService')}}</b-badge>
				<b-badge class="ml-2" variant="default" v-else><md-icon icon="home" /> <strong>{{$t('bookings.selfService')}}</strong></b-badge>
			</span>
		</template>
		<b-row class="mb-2">
			<b-col v-if="model.id>0 && !hasLimitedData">
				<small >
					<template v-if="model.creationUser !== null">
						<strong>{{$t('bookings.createdBy')}}: </strong> {{model.creationUser.firstName}}
						/ <strong>{{$t('bookings.reservationDate')}}:</strong> {{reservationDatetimeStr}}
					</template>
					<template v-if="model.cancelUser !== null">
						<br/>
						<strong>{{$t('bookings.canceledBy')}} por:</strong> {{model.cancelUser.firstName}}
						/ <strong>{{$t('bookings.cancelationDate')}}:</strong> {{reservationCancelDatetimeStr}}
					</template>
				</small>
			</b-col>
			<b-col class="text-right">
				<b-button v-if="model.id > 0" @click="copyCurrentRes" variant="dark" class="mr-2"><md-icon icon="library_add"/> {{$t('bookings.copyRes')}}</b-button>
				<b-button v-if="isCancelable" @click="showCancelModal = true" variant="danger"><md-icon icon="delete"/> {{$t('bookings.cancelRes')}}</b-button>
			</b-col>
		</b-row>
		<b-card no-body>
			<b-tabs
				no-fade
				pills
				card
				small
				:vertical="vertical"
				nav-class="nav-style"
			>
				<b-tab title="General" active>
					<form action="#" ref="resForm">
						<b-row>
							<b-col xl="4">
								<FormGroupBox :title="$t('bookings.clientInfo')">
									<b-form-row>
										<b-form-group class="col-6" :label="$t('global.firstName')">
											<b-form-input type="text" size="sm" v-model="model.clientFirstName" required :disabled="hasLimitedData || !canBeUpdated"/>
										</b-form-group>
										<b-form-group class="col-6" :label="$t('global.lastName')">
											<b-form-input type="text" size="sm" v-model="model.clientLastName" required :disabled="hasLimitedData || !canBeUpdated" />
										</b-form-group>
									</b-form-row>
									<b-form-row>
										<b-form-group class="col-6" :label="$t('global.phone')">
											<b-form-input type="text" size="sm" v-model="model.clientPhone" required :disabled="!canBeUpdated"/>
										</b-form-group>
										<b-form-group class="col-6" :label="$t('global.email')">
											<b-form-input type="text" size="sm" v-model="model.clientEmail" required :disabled="!canBeUpdated"/>
										</b-form-group>
									</b-form-row>
								</FormGroupBox>
								<FormGroupBox :title="$t('bookings.passengers')">
									<b-form-row>
										<b-form-group class="col-4" :label="$t('bookings.adults')">
											<b-form-select size="sm" v-model="model.adults" :disabled="hasLimitedData || !canBeUpdated">
												<b-form-select-option v-for="i in 100" :value="i" :key="i">{{i}}</b-form-select-option>
											</b-form-select>
										</b-form-group>
										<b-form-group class="col-4" :label="$t('bookings.kids')">
											<b-form-select size="sm" v-model="model.children" :disabled="hasLimitedData || !canBeUpdated">
												<b-form-select-option v-for="(item, i) in Array(50)" :value="i" :key="i">{{i}}</b-form-select-option>
											</b-form-select>
										</b-form-group>
										<b-form-group class="col-4" :label="$t('bookings.infants')">
											<b-form-select size="sm" v-model="model.infants" :disabled="hasLimitedData || !canBeUpdated">
												<b-form-select-option v-for="(item, i) in Array(50)" :value="i" :key="i">{{i}}</b-form-select-option>
											</b-form-select>
										</b-form-group>
									</b-form-row>
								</FormGroupBox>
								<b-row v-if="modelId > 0">
									<b-col>
										<b-overlay :show="loadingNotes" no-wrap rounded />
										<FormGroupBox v-if="!loadingNotes" :title="$t('bookings.notes')">
											<template #append-title>
												<b-button size="sm" @click="addNote = true" variant="success"><md-icon icon="add"/></b-button>
											</template>
											<Note
												v-if="addNote"
												:key="0"
												:id="-1"
												:creationDatetime="''"
												:name="user.firstName"
												:optUpdate="true"
												userApp="core"
												:content="''"
												:activeEdit="true"
												:reservation-id="model.id"
												@onSave="onSaveNote"
												@onDelete="onDeleteNote(-1)"
											/>
											<Note
												v-for="(note, index) in notes"
												:key="note.id"
												:id="note.id"
												:userApp="note.user.app"
												:creationDatetime="note.creationDatetime"
												:updateDatetime="note.updateDatetime"
												:name="note.user.firstName"
												:optUpdate="note.user.id == user.id && index == 0"
												:content="note.content"
												:activeEdit="note.id == -1"
												:reservation-id="model.id"
												@onSave="onSaveNote"
												@onDelete="onDeleteNote(note.id)"
											/>
										</FormGroupBox>
									</b-col>
								</b-row>
								<!-- Display review -->
								<b-row v-if="model.id > 0">
									<b-col>
										<FormGroupBox 
											:title="$t('bookings.reviews')">
											<Review 
												v-for="(transportService,tIndex) in model.transportServices"
												:key="tIndex"
												:modelId="transportService.id">
											</Review>
										</FormGroupBox>
									</b-col>
								</b-row>
								<!-- End display review -->
							</b-col>
							<b-col xl="8">
								<SingleTransportService
									v-for="(transportService,tIndex) in model.transportServices"
									:key="`${tIndex}.${transportService+1}`"
									:index="tIndex"
									:model="transportService"
									:enable-copy="canBeUpdated"
									:reservation="model"
									:operated-by="model.operatedBy"
									:has-limited-data="hasLimitedData"
									@clone="() => cloneTransportService(tIndex)"
									@delete="() => deleteTransportService(tIndex)"
									@onUpdate="() => fetchModel()"
								/>
								<div class="text-right">
									<b-button v-if="canBeUpdated" @click="addTransportService" class="mr-2" variant="success"><md-icon icon="add"/> {{$t('bookings.addService')}}</b-button>
								</div>
							</b-col>
						</b-row>
						<input type="submit" style="display: none"/>
					</form>
				</b-tab>

				<b-tab :title="$t('global.prices')" v-if="model.id > 0 && model.operatedBy == 'supplier'">
					<PricesModule :model-id="model.id"/>
				</b-tab>

				<b-tab :title="$t('global.changes')" v-if="model.id > 0 && model.operatedBy == 'supplier'">
					<History :model="model" :modelId="this.modelId"></History>
				</b-tab>				
			</b-tabs>
		</b-card>
		<SingleCancelModal
			:reservation-id="model.id"
			:show.sync="showCancelModal"
			@onCancel="fetchModel(); fetchNotes();"
		/>
	</b-modal>
</template>

<script>
import FormGroupBox from '../../helpers/FormGroupBox'
import SingleTransportService from './SingleTransportService'
import SingleCancelModal from './SingleCancelModal'
import Note from './Note'
import Resize from "../../helpers/Resize.js";
import Review from "../reviews/serviceReview.vue"
import PricesModule from './Prices'
import History from "./History.vue"

const modelDefault = {
	id:-1,
	clientFirstName:'',
	clientLastName:'',
	clientPhone:'',
	clientEmail:'',
	adults:1,
	children:0,
	infants:0,
	creationUser:null,
	cancelUser:null,
	clientEdited: false,
	clientConfirmed: false,
	transportServices : [
		{
			id:-1,
			serviceNumber:'',
			contactStatus:'noContacted',
			status:'active',
			pickupPlaceId:-1,
			dropoffPlaceId:-1,
			pickup:{ name: '' , isAirport:false},
			dropoff:{ name: '', isAirport:false},
			pickupDatetime:'',
			flightNumber:'',
			flightDatetime:'',
			serviceTypeId: -1,
			supplierSalesChannel: '',
			salesChannel : { id:-1, text:'' },
			supplierId:1,
			supplier : { id:1, text:'' },
			supplierReference:'',
			supplierNotes:'',
			salesChannelReference:'',
			flightValidationStatus:'none',
			flightValidationData:{},
			supplierPriceTotal: 0,
			supplierCurrency: 'usd',
			supplierPriceOverride:false,
			salesChannelId:-1,
			internalReference:"",
			driver:null

		}
	],
	operatedBy:'supplier',
	dataAccess:'full',
	travelersPortalUrl:'#',
	bookingPriceTotal:0,
	netPriceTotal:0,
	supplierPriceTotal:0,
	bookingCurrency:"",
	notes:[],
	status:'active',
}

export default {
	name:"ResSingle",
	mixins: [Resize],
	props: {
		modelId: {
			type : Number,
			required: false,
			default:-1
		},
		show: {
			type : Boolean,
			default:false
		},
		operatedBy: {
			type : String,
			default:'sc',
		},
	},
	components: {
		FormGroupBox,
		Note,
		SingleTransportService,
		SingleCancelModal,
		Review,
		PricesModule,
		History
	},
	data(){
		return {
			showCancelModal:false,
			transportServiceValidStatus:['active','pending'],
			changeServiceStatusId:'',
			changeServiceStatus:'canceled',
			changeStatusNote:'',
			changeStatusLoading:false,
			cancelResNote:'',
			cancelResLoading:false,
			addNote:false,
			loading:false,
			loadingNotes:true,
			model : JSON.parse(JSON.stringify(modelDefault)),
			modelDefault : { ...modelDefault },
			notes:[],
		}
	},
	computed: {
		hasLimitedData(){
			return this.model.operatedBy !== this.operatedBy
		},
		canBeUpdated(){
			return !this.hasLimitedData && this.model.status == 'active' && this.model.transportServices.filter(ts => this.transportServiceValidStatus.includes(ts.status)).length > 0
		},
		isCancelable(){
			return !this.hasLimitedData && this.model.status == 'active' && this.model.transportServices.filter(ts => !this.transportServiceValidStatus.includes(ts.status)).length === 0
		},
		statusLabel(){
			return this.$t(`bookings.serviceStatusOpts.${this.model.status}`)
		},
		user(){
			return this.$root.store.state.currentUser
		},
		statusOpts(){
			return this.$root.auxVars.reservationStatusOpts;
		},
		reservationDatetimeStr(){
			return this.$moment(this.model.reservationDatetime).format('DD/MMMM/YYYY HH:mm:ss')
		},
		reservationCancelDatetimeStr(){
			return this.$moment(this.model.cancelDatetime).format('DD/MMMM/YYYY HH:mm:ss')
		},
		changeServiceStatusLabel(){
			return this.$root.auxVars.transportServiceStatusOpts.find(aux => aux.value == this.changeServiceStatus).text
		},
		showModal:{
			get(){
				return this.show
			},
			set(val){
				this.$emit('update:show',val)
			}
		}
	},
	methods:{
		cloneTransportService(index){
			let aux = JSON.parse(JSON.stringify(this.model.transportServices[index]));
			aux.id = -1
			aux.status = 'active'
			aux.serviceNumber = ''
			this.model.transportServices.push(aux)
		},
		copyTravelersPortalUrl(){
			let field = this.$refs.travelersPortalUrl
			field.select()
			field.setSelectionRange(0, 999999)

			window.document.execCommand('copy')
			this.$root.showAlert('Link copiado!')
		},
		copyCurrentRes(){
			this.$emit('onCopyRes')
		},
		changeStatus(id,status){
			this.changeServiceStatusId = id
			this.changeServiceStatus = status
			this.$bvModal.show('change-status-service-modal')
		},
		doChangeStatus(){
			this.changeStatusLoading = true
			this.$root.api.post(`/transport-services/change-status/${this.changeServiceStatusId}`,{ note:this.changeStatusNote,status:this.changeServiceStatus }).then( () => {
				this.$root.showAlert('Servicio actualizado con éxito')
				this.changeStatusLoading = false
				this.changeStatusNote = ''
				this.fetchModel()
				this.fetchNotes()
				this.$bvModal.hide('change-status-service-modal')
				this.$root.$emit('bv::refresh::table', 'reservations-index-list')
			}).catch(this.$root.handleRequestError);
		},
		logsView(event){
			this.modalModelId = this.modelId
			this.$root.$emit('bv::show::modal', "modal-reservations-history-view", event.target)
		},
		onSaveNote(){
			this.addNote = false
			this.fetchNotes()
		},
		onDeleteNote(noteId){
			if(noteId == -1) this.addNote = false
			else{
				this.$root.showWarning({
					okAction: (event, setLoading, close) => {
						setLoading(true)
						this.$root.api.delete(`/reservation-notes/${noteId}`).then(() => {
							this.$root.showAlert('Nota Eliminada!',{ variant:'warning' })
							this.fetchNotes()
							close()
						}).catch( err => {
							this.$root.handleRequestError(err)
							close()
						});
					}
				})
			}
		},
		deleteTransportService(index){
			this.model.transportServices.splice(index,1)
		},
		addTransportService(){
			this.model.transportServices.push(JSON.parse(JSON.stringify(this.modelDefault.transportServices[0])))
		},
		fetchNotes(){
			this.notes = []
			if (this.modelId != -1) {
				this.loadingNotes = true
				this.$root.api.get(`/reservations/${this.modelId}/reservation-notes`).then( res => {
					this.notes = res.data
					this.loadingNotes = false
				}).catch(this.$root.handleRequestError);
			}
		},
		fetchModel(){
			this.model = JSON.parse(JSON.stringify(modelDefault))
			if (this.modelId != -1) {
				this.loading = true
				this.$root.api.get(`/reservations/${this.modelId}`).then( res => {
					this.model = {...res.data}
					this.model.clientFirstName = res.data.client.firstName
					this.model.clientLastName = res.data.client.lastName
					this.model.clientPhone = res.data.client.phone
					this.model.clientEmail = res.data.client.email
					this.model.clientLang = res.data.client.lang
					this.model.price = res.data.transportService

					this.loading = false
				}).catch((err)=>{
					if(err.response.status===404){
						this.$root.$emit("bv::hide::modal",	"modal-reservations-view");
					}
					this.$root.handleRequestError(err)
				});
			}
		},
		copyFromId(id){
			this.loading = true
			this.$root.api.get(`/reservations/${id}`).then( res => {
				let auxModel = {...res.data}
				this.cleanForCopy(auxModel)
				this.model = {...auxModel}
				this.loading = false
			}).catch(this.$root.handleRequestError);
		},
		cleanForCopy(model){
			model.id = -1
			model.status = 'active'
			model.operatedBy = this.operatedBy
			for(let i = 0;i<model.transportServices.length;i++){
				model.transportServices[i].id = -1
				model.transportServices[i].status = 'active'
				model.transportServices[i].serviceNumber = ''
			}
			this.notes = []
		},
		handleModelError(error){
			if(error.response && error.response.status == 422){
				let message = 'Error!'
					Object.keys(error.response.data).forEach(function(key) {
						message = error.response.data[key][0]
					});
				this.$root.showAlert(message,{variant:'danger'})
			}else{
				this.$root.handleRequestError(error)
			}
			this.loading = false;
		},
		onSubmit(){
			let aux = {
				id: this.model.id,
				clientFirstName:this.model.clientFirstName,
				clientLastName:this.model.clientLastName,
				clientPhone:this.model.clientPhone,
				clientEmail:this.model.clientEmail,
				adults:this.model.adults,
				children:this.model.children,
				infants:this.model.infants,
				transportServices: this.model.transportServices,
			}
			this.loading = true;
			if (this.modelId>0) {
				// actualizar
				this.$root.api.put(`/reservations/${this.model.id}`,aux).then(() => {
					this.loading = false;
					this.$root.showAlert('Actualización exitosa')
					this.$root.$emit('bv::refresh::table', 'reservations-index-list')
					this.fetchModel();
				}).catch(this.handleModelError)
			}else{
				this.$root.api.post(`/reservations`,aux).then((res) => {
					this.$root.showAlert('Reserva registrada con éxito')
					this.$root.$emit('bv::refresh::table', 'reservations-index-list')
					this.$emit('update:modelId',res.data.id);
				}).catch(this.handleModelError)
			}
		},
		trySubmitForm(event){
			event.preventDefault()
			if(this.$refs.resForm.checkValidity()){
				this.onSubmit()
			}else{
				this.$refs.resForm.reportValidity()
			}
		}
	},
	mounted(){
		// this.fetchModel();
	},
	watch:{
		modelId: function(){
			this.fetchModel();
			this.fetchNotes();
		},
	}
}
</script>

<style >
	.nav-style{
		padding: .5rem;
	}
	@media (min-width: 1200px){
		#modal-reservations-view .modal-dialog{
			max-width: 1300px;
		}
		.table{
			font-size: .85rem;
		}
	}
</style>
<style>
	._modalReservations {
		max-width: 95vw !important;
		/* Add any other styles you need */
	}
	@media (min-width: 1300px) {
		._modalReservations {
			max-width: 93vw !important;
		}
	}
	@media (min-width: 1600px) {
		._modalReservations {
			/* for big displays */
			max-width: 80vw !important;
			/* Add any other styles you need */
		}
	}
	
</style>
